import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiShield, FiShoppingBag, FiDollarSign, FiAlertCircle, FiChevronRight } from 'react-icons/fi';
import { getAdminDashboard } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminDashboard.css';


const STAT_CARDS = [
  { key: 'totalUsers',    label: 'Total Users',    icon: <FiUsers size={20} />,       color: '#6C47FF', bg: '#ede9ff' },
  { key: 'totalVendors',  label: 'Active Vendors',  icon: <FiShield size={20} />,      color: '#00D4AA', bg: '#e0faf5' },
  { key: 'totalOrders',   label: 'Total Orders',    icon: <FiShoppingBag size={20} />, color: '#f59e0b', bg: '#fef3c7' },
  { key: 'totalRevenue',  label: 'Revenue (Rs. )',      icon: <FiDollarSign size={20} />,  color: '#22c55e', bg: '#dcfce7', prefix: 'Rs. ' },
];

const STATUS_CONFIG = {
  placed:     { cls: 'badge-info' },
  confirmed:  { cls: 'badge-primary' },
  processing: { cls: 'badge-warning' },
  shipped:    { cls: 'badge-accent' },
  delivered:  { cls: 'badge-success' },
  cancelled:  { cls: 'badge-danger' },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await getAdminDashboard();
      const s = data.stats || {};
      const activity = data.recentActivity || {};
      setStats({
        totalUsers:    s.users || 0,
        totalVendors:  s.vendors?.approved || 0,
        pendingVendors: s.vendors?.pending || 0,
        totalOrders:   Object.values(s.orders || {}).reduce((a, b) => a + b, 0),
        totalRevenue:  s.revenue?.gross || 0,
      });
      setRecentOrders(activity.orders || []);
    } catch (err) {
      console.error('Admin dashboard error:', err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtValue = (card, val) => {
    if (val === undefined || val === null) return '—';
    if (card.key === 'totalRevenue') return `Rs. ${Number(val).toLocaleString('en-PK')}`;
    return String(val);
  };

  return (
    <AdminLayout title="Dashboard">
      <div className="adash">
        {/* Stat cards */}
        <div className="adash-stats">
          {STAT_CARDS.map((card) => (
            <div key={card.key} className="adstat-card card">
              <div className="adstat-icon" style={{ background: card.bg, color: card.color }}>{card.icon}</div>
              <div className="adstat-info">
                {loading
                  ? <div className="adstat-skeleton" />
                  : <p className="adstat-value">{fmtValue(card, stats?.[card.key])}</p>}
                <p className="adstat-label">{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pending vendors alert */}
        {!loading && stats?.pendingVendors > 0 && (
          <Link to="/admin/vendors?tab=pending" className="adash-alert">
            <FiAlertCircle size={18} className="adash-alert-icon" />
            <span>
              <strong>{stats.pendingVendors}</strong> vendor application{stats.pendingVendors > 1 ? 's' : ''} awaiting review
            </span>
            <FiChevronRight size={16} className="adash-alert-arrow" />
          </Link>
        )}

        {/* Recent orders */}
        <div className="adash-section">
          <div className="adash-section-header">
            <h2 className="adash-section-title">Recent Orders</h2>
            <Link to="/admin/orders" className="adsee-all">View All <FiChevronRight size={14} /></Link>
          </div>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : recentOrders.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <div className="empty-state-icon">📦</div>
              <h3>No orders yet</h3>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Buyer</th>
                    <th>Total</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => {
                    const { cls } = STATUS_CONFIG[order.orderStatus] || { cls: 'badge-info' };
                    const date = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
                    return (
                      <tr key={order._id}>
                        <td><p className="adorder-num">{order.orderNumber}</p></td>
                        <td className="font-medium">{order.buyer?.name || '—'}</td>
                        <td className="font-semi">Rs. {Number(order.total).toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}><span className={`badge ${cls}`}>{order.orderStatus}</span></td>
                        <td className="text-muted text-sm">{date}</td>
                        <td><Link to={`/orders/${order._id}`} className="btn btn-ghost btn-sm">View</Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick links */}
        <div className="adash-section">
          <h2 className="adash-section-title">Quick Actions</h2>
          <div className="adquick-grid">
            <Link to="/admin/vendors?tab=pending" className="adquick-card">
              <span className="adquick-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}><FiShield size={20} /></span>
              <span className="adquick-label">Review Applications</span>
            </Link>
            <Link to="/admin/vendors" className="adquick-card">
              <span className="adquick-icon" style={{ background: '#e0faf5', color: '#00D4AA' }}><FiUsers size={20} /></span>
              <span className="adquick-label">Manage Vendors</span>
            </Link>
            <Link to="/admin/orders" className="adquick-card">
              <span className="adquick-icon" style={{ background: '#ede9ff', color: '#6C47FF' }}><FiShoppingBag size={20} /></span>
              <span className="adquick-label">All Orders</span>
            </Link>
            <Link to="/admin/users" className="adquick-card">
              <span className="adquick-icon" style={{ background: '#dcfce7', color: '#22c55e' }}><FiUsers size={20} /></span>
              <span className="adquick-label">Manage Users</span>
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
